import { Helmet } from 'react-helmet-async'
import { BentoGrid, BentoGridItem } from '../components/ui/BentoGrid'
import GrantaEarthLogo from '../components/ui/GrantaEarthLogo'

const Stat = ({label, value}) => (
  <div className="text-center p-6 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
    <div className="text-3xl font-extrabold text-deep-green">{value}</div>
    <div className="mt-1 text-gray-600">{label}</div>
  </div>
)

export default function About() {
  return (
    <>
      <Helmet>
        <title>About | GrantaEarth Co.</title>
        <meta name="description" content="Leading global force in ethical trade of Africa's critical minerals - gold, lithium, antimony, and chrome. Bridging resource-rich communities with industrial demand across Eastern Europe, the Middle East, and Asia." />
      </Helmet>

      <section className="bg-gradient-to-b from-off-white to-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-8">
            <GrantaEarthLogo size="xl" className="mx-auto mb-6" />
          </div>
          
          <h1 className="text-3xl md:text-5xl font-extrabold bg-gradient-to-r from-deep-green to-copper bg-clip-text text-transparent text-center">
            Building dependable African supply for global partners
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-center text-gray-700">
            GrantaEarth operates across mining, energy trading, and cross border commodities. We combine disciplined execution with transparent governance to meet the standards of international investors and institutional buyers.
          </p>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <Stat label="Business units" value="3" />
            <Stat label="Focus regions" value="3" />
            <Stat label="Operating model" value="Contract based" />
          </div>

          {/* Vision & Mission Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal">Our Vision & Mission</h2>
              <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
                Driving ethical trade of Africa's critical minerals while empowering communities and advancing sustainable development.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
              {/* Vision */}
              <div className="bg-gradient-to-br from-deep-green/5 to-forest-green/10 rounded-2xl p-8 border border-deep-green/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-full bg-deep-green/10 flex items-center justify-center">
                    <span className="text-2xl">🌍</span>
                  </div>
                  <h3 className="text-2xl font-bold text-deep-green">Vision Statement</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  To become a leading global force in the ethical trade of Africa's critical minerals gold, lithium, antimony, and chrome bridging resource-rich communities with industrial demand across Eastern Europe, the Middle East, and Asia, while advancing the Sustainable Development Goals and empowering local economies.
                </p>
              </div>

              {/* Mission */}
              <div className="bg-gradient-to-br from-copper/5 to-deep-green/10 rounded-2xl p-8 border border-copper/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-full bg-copper/10 flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <h3 className="text-2xl font-bold text-copper">Mission Statement</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our mission is to responsibly source and trade strategic African minerals gold, lithium, antimony, and chrome through transparent, sustainable, and compliant supply chains.
                </p>
                <div className="space-y-3">
                  <h4 className="font-semibold text-charcoal">We are committed to:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-deep-green mt-1">•</span>
                      <span>Driving inclusive economic growth by investing in local communities and creating long-term opportunities.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-deep-green mt-1">•</span>
                      <span>Supporting global industries with high-quality, ethically sourced minerals essential for energy transition and technological advancement.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-deep-green mt-1">•</span>
                      <span>Aligning with the SDGs, particularly in promoting decent work, responsible consumption, gender equality, and climate resilience.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-deep-green mt-1">•</span>
                      <span>Building trusted global partnerships across Eastern Europe, the Middle East, and Asia through integrity, innovation, and shared value.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Core Values Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal">Core Values</h2>
              <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
                The principles that guide our operations and partnerships across Africa and global markets.
              </p>
            </div>

            <BentoGrid>
              <BentoGridItem 
                title="Ethical Sourcing" 
                description="Transparent supply chains with rigorous compliance frameworks ensuring responsible mineral extraction and trade practices."
                icon="✅"
              />
              <BentoGridItem 
                title="Community Empowerment" 
                description="Investing in local communities to create sustainable economic opportunities and long-term partnerships with resource-rich regions."
                icon="🤝"
              />
              <BentoGridItem 
                title="Global Excellence" 
                description="Meeting international standards for quality, documentation, and delivery across Eastern Europe, Middle East, and Asian markets."
                icon="🌍"
              />
              <BentoGridItem 
                title="Sustainable Development" 
                description="Advancing UN SDGs through responsible business practices that balance economic growth with environmental stewardship."
                icon="🌱"
              />
              <BentoGridItem 
                title="Innovation & Technology" 
                description="Supporting the global energy transition by supplying critical minerals essential for renewable energy and technological advancement."
                icon="⚡"
              />
              <BentoGridItem 
                title="Trusted Partnerships" 
                description="Building long-term relationships based on integrity, reliability, and shared value creation across international markets."
                icon="🛡️"
              />
            </BentoGrid>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-2">
            <div className="rounded-2xl border bg-white p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
              <h2 className="text-xl font-semibold">Operating Principles</h2>
              <ul className="mt-4 space-y-3 list-disc list-inside text-gray-700">
                <li>Ethical sourcing and environmental care</li>
                <li>Clear documentation and compliance</li>
                <li>Predictable supply backed by contracts</li>
                <li>Risk aware structures that protect counterparties</li>
              </ul>
            </div>
            <div className="rounded-2xl border bg-white p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
              <h2 className="text-xl font-semibold">Focus Areas</h2>
              <p className="mt-3 text-gray-700">
                Critical minerals trading across gold, lithium, antimony, and chrome with operations in Lalapanzi, Zvishavane, and Mutorashanga. Global partnerships spanning Eastern Europe, Middle East, and Asia.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
